/**
 * Technikal cart boosters — behaviour for snippets/tk-cart-extras.liquid.
 * Ported from Solara Covers (Be Yours theme, snippets/solara-interactions.liquid) onto Horizon.
 *
 * What it does:
 *  1. Switches ([data-tk-toggle]) for add-ons and shipping protection: tap = add / remove.
 *  2. Shipping protection rides along INSIDE the shopper's own add-to-cart request (no extra request,
 *     no flicker) — but only in countries where it is pre-ticked (theme setting) and only if the shopper
 *     never switched it off. In the EU it always starts OFF (pre-ticked paid extras are not allowed).
 *  3. Protection housekeeping: max quantity 1, removed when it's the only thing left in the cart.
 *  4. Checkout upsell: first Checkout click per visit opens the add-on panel if there is something new to offer.
 *
 * Cart changes go through Horizon's own Shopify.actions.updateCart, which re-renders the drawer and
 * cart page for us, so the Liquid state is always the truth. Never fire custom "refresh" loops here.
 */

const SP_OPT_OUT_KEY = 'tk_sp_opt_out';
const SP_ATTRIBUTE = '__tk_sp_opt_out'; // cart attribute: survives reloads / other tabs ("__" = hidden at checkout)
const UPSELL_SEEN_KEY = 'tk_cu_shown';

/* ---------------- small helpers ---------------- */

const storage = {
  get(store, key) {
    try { return window[store].getItem(key); } catch { return null; }
  },
  set(store, key, value) {
    try { value == null ? window[store].removeItem(key) : window[store].setItem(key, value); } catch { /* private mode */ }
  },
};

/** The cart-state element rendered by tk-cart-extras ('state' part). Drawer and cart page both render one. */
function cartState() {
  const el = document.querySelector('[data-tk-cart-state]');
  if (!el) return null;
  return {
    spVariant: el.dataset.spVariant || '',
    spKey: el.dataset.spKey || '',
    spQty: Number(el.dataset.spQty || 0),
    spPretick: el.dataset.spPretick === 'true',
    realQty: Number(el.dataset.realQty || 0),
  };
}

/** Cart count without the protection line (used by Horizon's cart-icon.js bubble). */
window.tkAdjustCount = (count) => Math.max(0, Number(count || 0) - (cartState()?.spQty || 0));

function spOptedOut() {
  return storage.get('localStorage', SP_OPT_OUT_KEY) === '1' || window.TK?.spOptOutAttribute === true;
}

/**
 * Change cart lines through Horizon's standard action (re-renders drawer + cart page).
 * lines: [{ merchandiseId, quantity }] to add, [{ id: lineKey, quantity }] to change/remove.
 */
async function updateLines(lines) {
  const actions = window.Shopify?.actions;
  if (actions?.updateCart) {
    const result = await actions.updateCart({ lines });
    if (result?.userErrors?.length) throw new Error(result.userErrors[0].message);
    return result;
  }
  // Fallback for older platform builds: AJAX API + full reload of the cart UI.
  for (const line of lines) {
    const url = line.id ? Theme.routes.cart_change_url : Theme.routes.cart_add_url;
    const body = line.id
      ? { id: line.id, quantity: line.quantity }
      : { items: [{ id: Number(line.merchandiseId), quantity: line.quantity }] };
    await fetch(`${url.replace(/\.js$/, '')}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
  }
  window.location.reload();
}

/** Remember the protection choice on the cart itself, fire-and-forget. */
function saveSpAttribute(optedOut) {
  if (window.TK) window.TK.spOptOutAttribute = optedOut;
  fetch(`${Theme.routes.cart_update_url}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes: { [SP_ATTRIBUTE]: optedOut ? '1' : '' } }),
  }).catch(() => {});
}

/* ---------------- 1. switches ---------------- */

document.addEventListener('click', async (event) => {
  const toggle = event.target.closest?.('[data-tk-toggle]');
  if (!toggle || toggle.getAttribute('aria-busy') === 'true') return;
  event.preventDefault();

  const turningOn = toggle.getAttribute('aria-checked') !== 'true';
  const kind = toggle.dataset.tkToggle; // 'addon' | 'sp'
  const variantId = toggle.dataset.variantId;
  const lineKey = toggle.dataset.lineKey;

  // Optimistic: flip now, the re-rendered cart confirms a moment later.
  toggle.setAttribute('aria-checked', String(turningOn));
  toggle.setAttribute('aria-busy', 'true');

  if (kind === 'sp') {
    storage.set('localStorage', SP_OPT_OUT_KEY, turningOn ? null : '1');
    saveSpAttribute(!turningOn);
  }

  try {
    if (turningOn) {
      await updateLines([{ merchandiseId: variantId, quantity: 1 }]);
    } else {
      let key = lineKey;
      if (!key) {
        const cart = await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());
        key = cart.items.find((item) => String(item.variant_id) === String(variantId))?.key;
      }
      if (key) await updateLines([{ id: key, quantity: 0 }]);
    }
    track(kind === 'sp' ? 'tk_shipping_protection' : 'tk_addon_toggle', { on: turningOn, variantId });
  } catch (error) {
    console.warn('[tk] cart update failed', error);
    toggle.setAttribute('aria-checked', String(!turningOn));
  } finally {
    toggle.removeAttribute('aria-busy');
  }
});

/* ---------------- 1b. offer progress button (cart drawer): add the missing free card(s) / gift in one tap ---------------- */
document.addEventListener('click', async (event) => {
  const btn = event.target.closest?.('[data-tk-add]');
  if (!btn || btn.getAttribute('aria-busy') === 'true') return;
  event.preventDefault();
  let items = [];
  try { items = JSON.parse(btn.dataset.tkAdd || '[]'); } catch { return; }
  if (!items.length) return;
  btn.setAttribute('aria-busy', 'true');
  try {
    await updateLines(items.map((i) => ({ merchandiseId: String(i.id), quantity: Number(i.q) || 1 })));
    track('tk_offer_add', { items });
  } catch (error) {
    console.warn('[tk] offer add failed', error);
  } finally {
    btn.removeAttribute('aria-busy');
  }
});

/* ---------------- 2. protection rides along with add-to-cart ---------------- */

const originalFetch = window.fetch.bind(window);

window.fetch = function tkFetch(input, init) {
  try {
    const url = typeof input === 'string' ? input : input?.url || '';
    const method = (init?.method || input?.method || 'GET').toUpperCase();
    if (method === 'POST' && /\/cart\/add(\.js)?(\?|$)/.test(url) && init?.body) {
      // a) combo partner chosen in the pack tiles (one-shot, like Solara's __scCartExtras)
      const extras = Array.isArray(window.__tkCartExtras) ? window.__tkCartExtras : [];
      for (const extra of extras) init = { ...init, body: withProtection(init.body, String(extra.id)) };
      window.__tkCartExtras = []; // consumed: re-armed by the next Add to cart click (tk-pdp.js)
      // b) shipping protection where it is pre-ticked
      const state = cartState();
      const shouldAdd =
        state && state.spVariant && state.spPretick && state.spQty === 0 && !spOptedOut();
      if (shouldAdd) init = { ...init, body: withProtection(init.body, state.spVariant) };
    }
  } catch (error) {
    console.warn('[tk] protection piggy-back skipped', error);
  }
  return originalFetch(input, init);
};

/** Returns the same add-to-cart body with the protection variant appended (FormData or JSON). */
function withProtection(body, spVariant) {
  if (body instanceof FormData) {
    if (body.has('items[0][id]')) {
      // already a multi-item request: append as the next index
      let i = 0;
      while (body.has(`items[${i}][id]`)) {
        if (body.get(`items[${i}][id]`) === spVariant) return body;
        i++;
      }
      body.append(`items[${i}][id]`, spVariant);
      body.append(`items[${i}][quantity]`, '1');
      return body;
    }
    if (!body.has('id')) return body;
    // single-item form → items[] form, keeping every field (properties, selling plan, sections…)
    const out = new FormData();
    for (const [key, value] of body.entries()) {
      if (key === 'id') out.append('items[0][id]', value);
      else if (key === 'quantity') out.append('items[0][quantity]', value);
      else if (key.startsWith('properties[')) out.append(`items[0][properties]${key.slice('properties'.length)}`, value);
      else if (key === 'selling_plan') out.append('items[0][selling_plan]', value);
      else out.append(key, value);
    }
    if (!out.has('items[0][quantity]')) out.append('items[0][quantity]', '1');
    out.append('items[1][id]', spVariant);
    out.append('items[1][quantity]', '1');
    // Horizon reads formData.get('id') afterwards from its own copy, so the original stays intact.
    return out;
  }
  if (typeof body === 'string' && body.trim().startsWith('{')) {
    const json = JSON.parse(body);
    if (!Array.isArray(json.items)) {
      if (json.id == null) return body;
      json.items = [{ id: json.id, quantity: json.quantity || 1, properties: json.properties }];
      delete json.id;
      delete json.quantity;
      delete json.properties;
    }
    if (json.items.some((item) => String(item.id) === spVariant)) return body;
    json.items.push({ id: Number(spVariant), quantity: 1 });
    return JSON.stringify(json);
  }
  return body;
}

/* ---------------- 3. protection housekeeping ---------------- */

let housekeepingTimer;
function protectionHousekeeping() {
  clearTimeout(housekeepingTimer);
  // wait for Horizon to morph the new cart HTML in
  housekeepingTimer = setTimeout(() => {
    const state = cartState();
    // header bubble: settle on the real product count (the protection line never counts)
    if (state) document.querySelector('cart-icon')?.renderCartBubble?.(state.realQty + state.spQty, false);
    if (!state || !state.spKey) return;
    if (state.realQty === 0) {
      updateLines([{ id: state.spKey, quantity: 0 }]).catch(() => {}); // alone in the cart → remove, never re-add
    } else if (state.spQty > 1) {
      updateLines([{ id: state.spKey, quantity: 1 }]).catch(() => {}); // hard cap: 1
    }
  }, 600);
}
document.addEventListener('shopify:cart:lines-update', (event) => {
  (event.promise || Promise.resolve()).finally(protectionHousekeeping);
});
document.addEventListener('DOMContentLoaded', protectionHousekeeping);

/* ---------------- 4. checkout upsell ---------------- */
// Solara-style: first Checkout click per visit opens a centred popup ABOVE the cart drawer (top-layer <dialog>).
function upsellDialog() {
  let dlg = document.getElementById('tk-upsell-dialog');
  if (!dlg) {
    dlg = document.createElement('dialog');
    dlg.id = 'tk-upsell-dialog';
    dlg.className = 'tk-upsell-dialog';
    dlg.setAttribute('aria-label', 'Add-ons');
    document.body.appendChild(dlg);
    dlg.addEventListener('click', (event) => {
      if (event.target === dlg || event.target.closest('[data-tk-upsell-close]')) dlg.close();
    });
  }
  return dlg;
}

document.addEventListener(
  'click',
  (event) => {
    const button = event.target.closest?.('[name="checkout"]');
    if (!button || storage.get('sessionStorage', UPSELL_SEEN_KEY) === '1') return;
    const scope = button.closest('dialog, .shopify-section') || document;
    const tpl = scope.querySelector('template[data-tk-upsell]') || document.querySelector('template[data-tk-upsell]');
    if (!tpl || Number(tpl.dataset.offers || 0) === 0) return;
    event.preventDefault();
    event.stopPropagation();
    storage.set('sessionStorage', UPSELL_SEEN_KEY, '1');
    const dlg = upsellDialog();
    dlg.replaceChildren(tpl.content.cloneNode(true));
    dlg.showModal();
    track('tk_checkout_upsell_shown', {});
  },
  true // capture: runs before the form submits
);

/* ---------------- analytics ---------------- */

/** Custom events for Shopify customer events / pixels (Solara tracked none of these). */
function track(name, data) {
  try { window.Shopify?.analytics?.publish?.(name, data); } catch { /* not available in editor */ }
}
